<script lang="ts">
  import { engine } from '../lib/player/engine.svelte.ts';
  import { api, type SessionStat } from '../lib/api/client.ts';
  import { platform, maxTiles } from '../lib/platform.ts';
  import { onMount } from 'svelte';

  let sessions = $state<SessionStat[]>([]);
  let sessionError = $state<string | null>(null);

  onMount(() => {
    const poll = async () => {
      try {
        sessions = (await api.sessions()).sessions;
        sessionError = null;
      } catch (err) {
        sessionError = err instanceof Error ? err.message : String(err);
      }
    };
    void poll();
    const timer = setInterval(poll, 3000);
    return () => clearInterval(timer);
  });

  const transcodes = $derived(sessions.filter((s) => s.kind === 'transcode').length);
</script>

<aside class="hud mono">
  <div class="row"><span>Plattform</span><b>{platform} · max {maxTiles} Kacheln</b></div>
  <div class="row"><span>Layout</span><b>{engine.effectiveLayout} · {engine.slots.length} aktiv</b></div>
  <div class="row">
    <span>Drops gesamt</span>
    <b class:bad={engine.totalDropped > 60} class:warn={engine.totalDropped > 0 && engine.totalDropped <= 60}>
      {engine.totalDropped}
    </b>
  </div>
  <div class="row">
    <span>ffmpeg</span>
    <b>{sessions.length} ({transcodes} Transcode)</b>
  </div>
  {#if engine.audioDisabledReason}
    <div class="row err"><span>Tonanalyse</span><b>abgeschaltet</b></div>
  {:else if engine.audioWatch}
    <div class="row">
      <span>Tonanalyse</span>
      <b>{engine.audioWatch.onBus} Kacheln · {engine.audioWatch.strikes} Stillstand</b>
    </div>
  {/if}
  {#if sessionError}
    <div class="row err"><span>Proxy</span><b>{sessionError}</b></div>
  {/if}

  <hr />

  {#each engine.slots as tile (tile.angle.id)}
    {@const s = tile.stats}
    <div class="tile-row">
      <span class="slot">{tile.slot}</span>
      <span class="label">{tile.angle.label}</span>
      {#if s}
        <span>{s.width}×{s.height}</span>
        <span>{s.mode ?? '—'}</span>
        <span class:warn={s.droppedFrames > 0}>drop {s.droppedFrames}</span>
        <span>buf {s.bufferSeconds.toFixed(1)}</span>
        <span class:faint={!s.audioActive}>
          {s.audioActive ? `ton ${(s.rms ?? 0).toFixed(3)}` : 'ton —'}
        </span>
        <span class:faint={s.behindRealTime === null}>
          {s.behindRealTime !== null ? `${s.behindRealTime.toFixed(0)}s hinter live` : 'ohne Zeitstempel'}
        </span>
      {:else}
        <span class="faint">—</span>
      {/if}
    </div>
  {/each}

  <p class="note faint">
    Bleiben die Drops nahe null, trägt die Hardware dieses Layout. Steigen sie stetig,
    dekodiert eine Kachel in Software: Layout verkleinern oder Kachelhöhe im Proxy senken.
  </p>
</aside>

<style>
  .hud {
    position: absolute;
    top: var(--safe-inset);
    right: var(--safe-inset);
    width: min(32em, 46vw);
    padding: var(--pad);
    background: rgba(5, 8, 12, 0.93);
    border: 1px solid var(--border-strong);
    border-radius: var(--radius);
    font-size: var(--font-sm);
    pointer-events: none;
    z-index: 20;
  }

  .row, .tile-row { display: flex; gap: var(--gap); align-items: baseline; }
  .row { justify-content: space-between; }
  .row span, .tile-row span { color: var(--text-dim); }
  .row b { color: var(--text); font-weight: 600; }

  .tile-row { justify-content: flex-start; flex-wrap: wrap; font-size: 0.92em; }
  .tile-row .slot {
    min-width: 1.6em; text-align: center;
    background: rgba(255, 255, 255, 0.12);
    border-radius: 3px; color: var(--text);
  }
  .tile-row .label { flex: 1 1 7em; color: var(--text); }

  hr { border: none; border-top: 1px solid var(--border); margin: var(--gap) 0; }

  .warn { color: var(--warn) !important; }
  .bad { color: var(--danger) !important; }
  .err b { color: var(--danger); }

  .note { margin: var(--gap) 0 0; font-size: 0.9em; line-height: 1.4; }
</style>
